import { addDays, addMinutes, addYears, formatISO, isAfter, isBefore, set } from 'date-fns'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import sync from '@/sync/service'
import type { ClosingTime, VirtualBrand } from '@/types/virtualBrands'

import { useConfigStore } from './config'

export const useDeliveryCompaniesStore = defineStore('deliveryCompanies', () => {
  const configStore = useConfigStore()

  const { config } = storeToRefs(configStore)

  const virtualBrandsClosingTimes = ref<VirtualBrand[]>([])

  const sortedClosedUntil = computed(() => {
    return virtualBrandsClosingTimes.value
      .flatMap(brand => Object.values(brand.externalDeliveryCompaniesClosingTimes))
      .filter(time => time !== null)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  })

  const virtualBrandsWithClosedDeliveries = computed(() => {
    return virtualBrandsClosingTimes.value
      .map(brand => {
        return {
          ...brand,
          externalDeliveryCompaniesClosingTimes: Object.entries(
            brand.externalDeliveryCompaniesClosingTimes
          ).reduce(
            (res, [key, value]) => {
              if (value && isAfter(new Date(value), new Date())) {
                res[key] = value
              }
              return res
            },
            {} as Record<string, string>
          )
        }
      })
      .filter(brand => Object.values(brand.externalDeliveryCompaniesClosingTimes).length > 0)
  })

  const bookingsBrand = computed(() => {
    const brand = virtualBrandsClosingTimes.value.find(
      brand => 'Bookings' in brand.externalDeliveryCompaniesClosingTimes
    )
    if (!brand) return null
    return {
      id: brand.id,
      brandName: brand.brandName,
      closedUntil: brand.externalDeliveryCompaniesClosingTimes['Bookings']
    }
  })

  const hasDeliveries = computed(() => {
    return (
      virtualBrandsClosingTimes.value.filter(
        brand => Object.values(brand.externalDeliveryCompaniesClosingTimes).length > 0
      ).length > 0
    )
  })

  function refreshClosingTimes(newVirtualBrandsClosingTimes: VirtualBrand[]) {
    virtualBrandsClosingTimes.value = newVirtualBrandsClosingTimes
  }

  function openDeliveries(deliveriesToOpen: Record<string, string[]>) {
    sync.record('openDeliveries', {
      deliveriesToOpen
    })
  }

  function closeDeliveries(deliveriesToClose: Record<string, string[]>, closingTime: ClosingTime) {
    let closedUntil
    if (!isNaN(closingTime as number)) {
      closedUntil = addMinutes(new Date(), closingTime as number)
    } else if (closingTime === 'indefinitely') {
      closedUntil = addYears(new Date(), 10)
    } else {
      const endTime = config.value.workingTime!.end.split(':')
      const endHour = parseInt(endTime[0])
      const endMinute = parseInt(endTime[1])
      closedUntil = set(new Date(), {
        hours: endHour,
        minutes: endMinute
      })
      if (isBefore(closedUntil, new Date())) {
        closedUntil = addDays(closedUntil, 1)
      }
    }
    closedUntil = formatISO(closedUntil, { representation: 'complete' })

    sync.record('deliveriesClosed', {
      deliveriesToClose,
      closedUntil
    })

    return closedUntil
  }

  return {
    virtualBrandsClosingTimes,
    sortedClosedUntil,
    virtualBrandsWithClosedDeliveries,
    hasDeliveries,
    bookingsBrand,
    refreshClosingTimes,
    openDeliveries,
    closeDeliveries
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDeliveryCompaniesStore, import.meta.hot))
}
